import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import App from 'pages/account/template';
import Subtitle from 'components/Subtitle';
import Button from 'components/Button';
import Loader from 'components/SectionLoader';
import Fade from 'components/Fade';
import TwoFactorChannelCard from 'components/cards/TwoFactorChannelCard';
import { STATUS_LOADING } from 'features/userSlice';
import { formatTwoFactorExemptUntilLabels } from 'utils/format';

const MEMBER_CORPORATE_INFO_PATH = '/account/member/edit/corporate-info';

function memberRequiresTwoFactorForChannel(channel, { activeOrganization, organizationProfile }) {
  const memberRoleIds = new Set((organizationProfile?.roles || []).map((role) => Number(role.id)));

  if (memberRoleIds.size === 0) {
    return false;
  }

  const isPortal = channel === 'portal';
  const isEnabled = isPortal
    ? Boolean(activeOrganization?.enable_2fa_portal)
    : Boolean(activeOrganization?.enable_2fa_app);
  const requiredRoleIds = isPortal
    ? activeOrganization?.enable_2fa_roles_portal || []
    : activeOrganization?.enable_2fa_roles_app || [];

  if (!isEnabled || requiredRoleIds.length === 0) {
    return false;
  }

  return requiredRoleIds.some((roleId) => memberRoleIds.has(Number(roleId)));
}

function resolveTwoFactorChannelState(channel, { requiresTwoFactor, isExempt }) {
  const isTemporarilyInactive = requiresTwoFactor && isExempt;
  const isActive = requiresTwoFactor && !isExempt;

  let badgeVariant = 'secondary';
  if (isTemporarilyInactive) {
    badgeVariant = 'danger';
  } else if (isActive) {
    badgeVariant = 'success';
  }

  let descriptionKey;
  if (channel === 'portal') {
    descriptionKey = isActive
      ? 'text-two-factor-authentication-enabled-portal'
      : 'text-two-factor-authentication-not-enabled-portal';
  } else {
    descriptionKey = isActive
      ? 'text-two-factor-authentication-enabled-app'
      : 'text-two-factor-authentication-not-enabled-app';
  }

  let methodColor = 'secondary';
  if (isActive) {
    methodColor = 'success';
  } else if (isTemporarilyInactive) {
    methodColor = 'error';
  }

  let methodValueKey = 'two-factor-method-inactive';
  if (isActive) {
    methodValueKey = 'two-factor-method-active';
  } else if (isTemporarilyInactive) {
    methodValueKey = 'two-factor-method-temporarily-inactive';
  }

  return {
    badgeVariant,
    descriptionKey,
    methodColor,
    methodValueKey,
  };
}

function AccountSecurity() {
  const user = useSelector((state) => /** @type {any} */ (state).user);
  const activeOrganization = user.active_organization;
  const organizationProfile = user.organization_profile;
  const twoFactorEmail = organizationProfile?.email || '-';
  const ref = useRef(null);

  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const twoFactorExemptUntilRaw = organizationProfile?.two_factor_exempt_until ?? null;
  const twoFactorExemptLabels = useMemo(
    () =>
      twoFactorExemptUntilRaw ? formatTwoFactorExemptUntilLabels(twoFactorExemptUntilRaw, i18n.language) : null,
    [twoFactorExemptUntilRaw, i18n.language],
  );
  const hasTwoFactorExemption = Boolean(twoFactorExemptLabels);
  const portalRequiresTwoFactor = memberRequiresTwoFactorForChannel('portal', {
    activeOrganization,
    organizationProfile,
  });
  const appRequiresTwoFactor = memberRequiresTwoFactorForChannel('app', {
    activeOrganization,
    organizationProfile,
  });
  const hasTwoFactorRequirement = portalRequiresTwoFactor || appRequiresTwoFactor;
  const isTwoFactorExempt = hasTwoFactorRequirement && hasTwoFactorExemption;

  const portalChannel = resolveTwoFactorChannelState('portal', {
    requiresTwoFactor: portalRequiresTwoFactor,
    isExempt: isTwoFactorExempt,
  });
  const appChannel = resolveTwoFactorChannelState('app', {
    requiresTwoFactor: appRequiresTwoFactor,
    isExempt: isTwoFactorExempt,
  });

  const twoFactorMethodLabel = t('account.edit-security.two-factor-method-label');

  useEffect(() => {
    if (isLoadingPage && ref.current === STATUS_LOADING && user.status !== STATUS_LOADING) {
      setIsLoadingPage(false);
    }

    ref.current = user.status;
  }, [user.status, isLoadingPage]);

  return (
    <App>
      <Fade>
        <Box maxWidth="428px" data-testid="account-security-page">
          <Subtitle className="">{t('account.edit-security.menu-security')}</Subtitle>
          <Loader loading={isLoadingPage}>
            <Fade>
              <Typography variant="body2" color="secondary" marginBottom="32px">
                {t('account.edit-security.text-security')}
              </Typography>

              {hasTwoFactorRequirement ? (
                <Box marginBottom={2.5}>
                  <Typography variant="body2" color="secondary" data-testid="two-factor-email" component="div">
                    {t('account.edit-security.two-factor-code-email', { email: twoFactorEmail })}{' '}
                    <MuiLink
                      component={Link}
                      to={MEMBER_CORPORATE_INFO_PATH}
                      variant="body2"
                      data-testid="two-factor-edit-email-link"
                    >
                      {t('account.edit-security.two-factor-edit-email-link')}
                    </MuiLink>
                  </Typography>
                </Box>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  gap: 2.5,
                }}
              >
                <TwoFactorChannelCard
                  data-testid="two-factor-portal-readonly"
                  badgeVariant={portalChannel.badgeVariant}
                  title={t('organization.organization-settings.security-policy.section-portal-title')}
                  methodLabel={twoFactorMethodLabel}
                  methodValue={t(`account.edit-security.${portalChannel.methodValueKey}`)}
                  methodColor={portalChannel.methodColor}
                  description={t(`account.edit-security.${portalChannel.descriptionKey}`)}
                />

                <TwoFactorChannelCard
                  data-testid="two-factor-app-readonly"
                  badgeVariant={appChannel.badgeVariant}
                  title={t('organization.organization-settings.security-policy.section-app-title')}
                  methodLabel={twoFactorMethodLabel}
                  methodValue={t(`account.edit-security.${appChannel.methodValueKey}`)}
                  methodColor={appChannel.methodColor}
                  description={t(`account.edit-security.${appChannel.descriptionKey}`)}
                />
              </Box>

              {isTwoFactorExempt ? (
                <Typography
                  variant="body2"
                  color="error"
                  marginTop={2.5}
                  data-testid="two-factor-exemption-notice"
                >
                  {t('account.edit-security.two-factor-exemption-notice', {
                    ...twoFactorExemptLabels,
                    interpolation: { escapeValue: false },
                  })}
                </Typography>
              ) : null}

              <Box marginTop="32px">
                <Button sx={{}} variant="outlined" onClick={() => navigate('/')} data-testid="cancel-button">
                  {t('account.edit-security.button-cancel')}
                </Button>
              </Box>
            </Fade>
          </Loader>
        </Box>
      </Fade>
    </App>
  );
}

export default AccountSecurity;
